import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  DialogTitle as MuiDialogTitle,
  DialogActions as MuiDialogActions,
  Breakpoint as MuiBreakpoint
} from "@mui/material";
import Typography from "../typography";
import Flex from "../flex";
import Button from "../button";
import { useEffect, useRef, useState, PropsWithChildren, ReactNode } from "react";

import { AlertProps } from "flux";

import { BasicDialogLayout, TabDialogLayout } from "./dialog-layout-options";

export type DialogSize = MuiBreakpoint;
type FormRef = React.RefObject<HTMLFormElement | null>;

interface DialogAction {
  label: string;
  onClick: () => void;
  type?: "cancel" | "destructive" | "default";
  loading?: boolean;
  disabled?: boolean;
}

type DialogActionButtonConfig = {
  variant: "outlined" | "contained";
  color: "secondary" | "error";
};

const actionButtonConfigs: Record<string, DialogActionButtonConfig> = {
  cancel: {
    variant: "outlined",
    color: "secondary"
  },
  destructive: {
    variant: "outlined",
    color: "error"
  },
  default: {
    variant: "outlined",
    color: "secondary"
  }
};

interface BaseDialogProps extends Omit<MuiDialogProps, "onSubmit"> {
  title?: string;
  titleImage?: ReactNode;
  productPreviews?: ReactNode;
  primaryButtonText?: string;
  isPrimaryButtonDisabled?: boolean;
  isPrimaryButtonLoading?: boolean;
  open: boolean;
  onClose: (confirmed: boolean) => void;
  description?: string;
  size?: DialogSize;
  danger?: boolean;
  compact?: boolean /* TODO: REMOVE THIS */;
  compactHeader?: boolean;
  showCancel?: boolean;
  wideButtons?: boolean;
  dialogAlert?: { type: AlertProps["severity"]; message: string };
  cancelButtonText?: string;
  onCancel?: () => void;
  actions?: Array<DialogAction>;
  dataDogIdentifier: string;
}

interface ButtonRowProps {
  onClose: (confirmed: boolean) => void;
  open: boolean;
  primaryButtonText: string;
  isPrimaryButtonDisabled?: boolean;
  isPrimaryButtonLoading?: boolean;
  danger?: boolean;
  compact?: boolean;
  formRef?: FormRef;
  showCancel?: boolean;
  wideButtons?: boolean;
  cancelButtonText?: string;
  onCancel?: () => void;
  actions?: Array<DialogAction>;
  dataDogIdentifier: string;
  tabsClearAllButton?: boolean;
  onClearAll?: () => void;
}

function ButtonRow({
  onClose,
  open,
  primaryButtonText,
  isPrimaryButtonDisabled = false,
  isPrimaryButtonLoading = false,
  danger = false,
  compact = false,
  formRef,
  showCancel = true,
  wideButtons = false,
  actions,
  dataDogIdentifier,
  tabsClearAllButton = false,
  onClearAll = () => {}
}: ButtonRowProps): React.JSX.Element {
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        primaryButtonRef.current?.focus();
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [open]);

  const handlePrimaryButtonClick = () => {
    if (formRef?.current) {
      formRef.current.requestSubmit();
    } else {
      onClose(true);
    }
  };

  return (
    <>
      {tabsClearAllButton && (
        <Button
          variant="text"
          color="info"
          sx={{ width: wideButtons ? "200px" : undefined }}
          dataDogIdentifier={`${dataDogIdentifier} tab dialog clear all button`}
          onClick={onClearAll}
        >
          CLEAR ALL
        </Button>
      )}
      <Flex
        flexDirection="row"
        justifyContent="end"
        columnGap={2}
        width="100%"
        height={compact ? "72px" : undefined}
        px="32px"
        py={compact ? "16px" : "24px"}
        bgcolor={compact ? "fillGray.main" : undefined}
      >
        {actions &&
          actions.map((action, index) => (
            <Button
              key={index}
              sx={{ width: wideButtons ? "200px" : undefined }}
              {...actionButtonConfigs[action.type || "default"]}
              onClick={action.onClick}
              loading={action.loading}
              disabled={action.disabled}
              dataDogIdentifier={`${dataDogIdentifier} dialog action button`}
            >
              {action.label}
            </Button>
          ))}
        {showCancel && (
          <Button
            sx={{ width: wideButtons ? "200px" : undefined }}
            variant="outlined"
            color="secondary"
            onClick={() => onClose(false)}
            type="button"
            dataDogIdentifier={`${dataDogIdentifier} dialog cancel button`}
          >
            Cancel
          </Button>
        )}
        <Button
          ref={primaryButtonRef}
          variant="contained"
          color={danger ? "error" : "secondary"}
          onClick={handlePrimaryButtonClick}
          disabled={isPrimaryButtonDisabled}
          loading={isPrimaryButtonLoading}
          type="button"
          sx={{ width: showCancel && !wideButtons ? undefined : "200px" }}
          dataDogIdentifier={`${dataDogIdentifier} dialog submit button`}
        >
          {primaryButtonText}
        </Button>
      </Flex>
    </>
  );
}

export interface ExtendedBaseDialogProps extends BaseDialogProps {
  formRef?: FormRef;
  dataDogIdentifier: string;
  tabs?: Array<{ label: string | ReactNode; content: ReactNode; title?: string; description?: string }>;
  onClearAll?: () => void;
}

const BaseDialog = ({ ...props }: PropsWithChildren<ExtendedBaseDialogProps>): React.JSX.Element => {
  const {
    onClose,
    open,
    size = "md",
    title,
    titleImage,
    description,
    compact,
    formRef,
    primaryButtonText,
    showCancel,
    compactHeader,
    sx,
    productPreviews,
    dialogAlert,
    cancelButtonText,
    onCancel,
    actions,
    dataDogIdentifier,
    tabs,
    wideButtons = false,
    onClearAll = () => {},
    // Forwarded to MUI's Modal focus trap. Needed when this dialog hosts a
    // nested non-MUI (e.g. Radix) dialog: MUI enforces focus back inside itself
    // by default, which steals focus from the nested dialog's inputs and makes
    // them require a second click to focus/blur.
    disableEnforceFocus,
    ...rest
  } = props;

  const contentPadding = compact ? "24px" : "32px";
  const headerPadding = compactHeader ? "4px" : "32px";

  return (
    <MuiDialog
      onClose={() => onClose(false)}
      open={open}
      disableEnforceFocus={disableEnforceFocus}
      maxWidth={size}
      fullWidth={true}
      PaperProps={{
        style: {
          borderRadius: "12px",
          height: tabs ? "996px" : "auto",
          display: "flex",
          flexDirection: "column"
        }
      }}
      sx={{
        ...sx,
        "& .MuiDialogContent-root": {
          paddingX: compact ? "24px" : "32px",
          paddingY: !tabs || !title ? "24px" : 0
        },
        ...(tabs && {
          "& .MuiDialog-paper": {
            maxWidth: "none",
            width: "100%"
          }
        })
      }}
      data-action-name={`${dataDogIdentifier} dialog`}
    >
      {tabs ? (
        <TabDialogLayout
          contentPadding={contentPadding}
          tabs={tabs}
          dialogAlert={dialogAlert}
          title={title}
          description={description}
          titleImage={titleImage}
          productPreviews={productPreviews}
          dataDogIdentifier={dataDogIdentifier}
          open={open}
          onClose={onClose}
          {...rest}
        />
      ) : (
        <BasicDialogLayout
          contentPadding={contentPadding}
          headerPadding={headerPadding}
          title={title}
          description={description}
          titleImage={titleImage}
          productPreviews={productPreviews}
          dialogAlert={dialogAlert}
          dataDogIdentifier={dataDogIdentifier}
          open={open}
          onClose={onClose}
          {...rest}
        />
      )}
      {primaryButtonText && (
        <BaseDialogFooter
          tabsDivider={!!tabs}
          primaryButtonText={primaryButtonText}
          onClose={onClose}
          open={open}
          compact={compact}
          formRef={formRef}
          showCancel={showCancel}
          cancelButtonText={cancelButtonText}
          onCancel={onCancel}
          actions={actions}
          dataDogIdentifier={dataDogIdentifier}
          wideButtons={wideButtons}
          onClearAll={onClearAll}
          {...rest}
        />
      )}
    </MuiDialog>
  );
};

interface BaseDiagloHeaderProps {
  title?: string;
  titleImage?: ReactNode;
  description?: string;
  productPreviews?: ReactNode;
  contentPadding: string;
  headerPadding: string;
}
export const BaseDiagloHeader = ({
  title,
  titleImage,
  description,
  productPreviews,
  contentPadding,
  headerPadding
}: BaseDiagloHeaderProps) => {
  return (
    <MuiDialogTitle sx={{ fontSize: "32px", lineHeight: "40px", px: contentPadding, pt: "32px", pb: headerPadding }}>
      <Flex justifyContent="space-between">
        <Flex>
          {titleImage}
          <Flex flexDirection="column" justifyContent="center" gap="4px" mb="10px">
            {<Typography variant="h1">{title}</Typography>}
            {description && <Typography variant="body1">{description}</Typography>}
          </Flex>
        </Flex>
        {productPreviews}
      </Flex>
    </MuiDialogTitle>
  );
};

interface BaseDialogFooterProps {
  primaryButtonText: string;
  onClose: (confirmed: boolean) => void;
  open: boolean;
  compact?: boolean;
  formRef?: FormRef;
  showCancel?: boolean;
  cancelButtonText?: string;
  onCancel?: () => void;
  actions?: Array<DialogAction>;
  dataDogIdentifier: string;
  tabsDivider?: boolean;
  wideButtons?: boolean;
  onClearAll?: () => void;
}
export const BaseDialogFooter = ({
  primaryButtonText,
  onClose,
  open,
  compact,
  formRef,
  showCancel,
  cancelButtonText,
  onCancel,
  actions,
  dataDogIdentifier,
  tabsDivider = false,
  wideButtons = false,
  onClearAll = () => {},
  ...rest
}: BaseDialogFooterProps) => {
  return (
    <MuiDialogActions
      sx={{
        padding: 0,
        margin: 0,
        borderTop: tabsDivider ? "1px solid" : "none",
        borderColor: tabsDivider ? "divider" : "transparent"
      }}
    >
      <ButtonRow
        {...rest}
        primaryButtonText={primaryButtonText}
        onClose={onClose}
        open={open}
        compact={compact}
        formRef={formRef}
        showCancel={showCancel}
        cancelButtonText={cancelButtonText}
        onCancel={onCancel}
        actions={actions}
        wideButtons={wideButtons}
        dataDogIdentifier={dataDogIdentifier}
        tabsClearAllButton={tabsDivider}
        onClearAll={onClearAll}
      />
    </MuiDialogActions>
  );
};

export type DialogProps = ExtendedBaseDialogProps;

export const Dialog = ({ children, ...props }: PropsWithChildren<DialogProps>): React.JSX.Element => {
  return <BaseDialog {...props}>{children}</BaseDialog>;
};

export interface FormDialogProps extends BaseDialogProps {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
  actions?: Array<DialogAction>;
  dataDogIdentifier: string;
}

export const FormDialog = ({ children, ...props }: PropsWithChildren<FormDialogProps>): React.JSX.Element => {
  const { onSubmit, dataDogIdentifier, ...rest } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    await onSubmit(event).finally(() => setIsSubmitting(false));
  };

  return (
    <BaseDialog isPrimaryButtonLoading={isSubmitting} {...rest} formRef={formRef} dataDogIdentifier={dataDogIdentifier}>
      <form ref={formRef} onSubmit={handleSubmit} style={{ width: "100%" }}>
        {children}
      </form>
    </BaseDialog>
  );
};
